import { useEffect, useState } from 'react';
import { Download, Receipt } from 'lucide-react';
import { useLang } from '../../../i18n';
import { get, post } from '../../../services/api';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import Badge from '../../../components/Badge';
import FileDrop from '../../../components/FileDrop';
import EmptyState from '../../../components/EmptyState';
import Skeleton from '../../../components/Skeleton';
import type { FileDropItem } from '../../../components/FileDrop/FileDrop.types';
import type { Paginated } from '../../../types';

type InvoiceStatus = 'issued' | 'paid' | 'overdue';

interface Invoice {
  public_id: string;
  number: string;
  amount: string;
  currency: string;
  status: InvoiceStatus;
}

interface Props { publicId: string }

function statusVariant(s: InvoiceStatus): 'brand' | 'success' | 'danger' {
  if (s === 'paid') return 'success';
  if (s === 'overdue') return 'danger';
  return 'brand';
}

export default function OrderInvoices({ publicId }: Props) {
  const { t } = useLang();
  const [items, setItems] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState<Record<string, FileDropItem[]>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await get<Paginated<Invoice>>(`/me/orders/${publicId}/invoices/`);
        if (!cancelled) setItems(res.results || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicId]);

  const onDownload = async (id: string, kind: 'invoice' | 'act') => {
    try {
      const res = await post<{ url: string }>(`/me/invoices/${id}/${kind}/`);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch { void 0; }
  };

  const onAdd = async (id: string, files: File[]) => {
    const next: FileDropItem[] = files.map((f) => ({ id: `${Date.now()}-${f.name}`, file: f }));
    setReceipts((r) => ({ ...r, [id]: [...(r[id] || []), ...next] }));
    const fd = new FormData();
    fd.append('file', files[0]);
    try { await post(`/me/invoices/${id}/receipts/`, fd); } catch { void 0; }
  };

  const onRemove = (id: string, fid: string) => {
    setReceipts((r) => ({ ...r, [id]: (r[id] || []).filter((x) => x.id !== fid) }));
  };

  if (loading) return <Skeleton height={120} />;
  if (items.length === 0) {
    return <Card padding="lg"><EmptyState icon={<Receipt size={36} />} title="—" /></Card>;
  }

  return (
    <div className="order-invoices">
      {items.map((inv) => (
        <Card key={inv.public_id} padding="md" className="order-invoices__item">
          <div className="order-invoices__head">
            <div className="order-invoices__info">
              <div className="order-invoices__number">{t('order.invoice.number', { n: inv.number })}</div>
              <div className="order-invoices__amount">{inv.amount} {inv.currency}</div>
            </div>
            <Badge variant={statusVariant(inv.status)}>{t(`order.invoice.status.${inv.status}`)}</Badge>
          </div>
          <div className="order-invoices__actions">
            <Button variant="secondary" size="sm" iconLeft={<Download size={14} />} onClick={() => onDownload(inv.public_id, 'invoice')}>
              {t('order.invoice.download_pdf')}
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Download size={14} />} onClick={() => onDownload(inv.public_id, 'act')}>
              {t('order.invoice.download_act')}
            </Button>
          </div>
          <FileDrop
            items={receipts[inv.public_id] || []}
            onAdd={(files) => onAdd(inv.public_id, files)}
            onRemove={(fid) => onRemove(inv.public_id, fid)}
            label={t('order.invoice.attach_receipt')}
          />
        </Card>
      ))}
    </div>
  );
}
